import type { Ton } from '../commun.js'
import { SALLES_CLUB, nomSalle } from '../salles.js'

/**
 * FICHES DE SUPERVISION — LE MODÈLE PUR (21/10/2026).
 *
 * Formes de Sonde (contrat v1, relayées TELLES QUELLES par
 * `/api/v1/supervision/v/…`) pour les fiches salle, appareil et incident, et
 * les quelques décisions d'affichage qui se testent sans navigateur :
 *
 *   · un trou est un trou — une courbe se coupe dès qu'un point manque ou
 *     qu'un écart dépasse `gap_threshold_s` ; jamais un zéro inventé ;
 *   · une action n'apparaît que si l'état de l'incident la permet côté Sonde
 *     (acquitter : ouvert et pas encore acquitté ; résoudre : pas résolu) ;
 *   · un lien interne venu de Sonde n'est suivi que s'il reste sous
 *     `/infrastructure` ;
 *   · « — » pour une valeur absente, jamais 0.
 */

/* ═══════════════ Formes de Sonde ═══════════════ */

export type EtatAffiche = 'ONLINE' | 'DEGRADED' | 'OFFLINE' | 'OFF_EXPECTED' | 'MAINTENANCE' | 'UNKNOWN' | 'STALE' | 'UNAVAILABLE'

export interface Collecteur { ok: boolean; stale: boolean; heartbeat_age_s: number | null; code: string | null; detail_fr: string | null }

export interface EtatAppareil {
  display_state: EtatAffiche
  display_label: string
  greyed: boolean
  status: string | null
  observation: 'FRESH' | 'STALE' | 'SOURCE_UNAVAILABLE' | null
  cause: string | null
  cause_label: string | null
  cause_ref: string | null
  cause_uid: string | null
  since: string | null
  observed_at: string | null
  data_age_s: number | null
  flapping: boolean
  confidence: number | null
  exception: { kind: string; reason: string; until: string | null } | null
}

export interface AppareilBref {
  uid: string
  display_name: string
  kind: string
  family: string
  family_label: string
  zone_code: string | null
  zone_detail: string | null
  lifecycle: string
  criticality: string
  weighted: boolean
  visibility: string
  tracking: string
  state: EtatAppareil
}

export interface Comptes {
  total: number; online: number; degraded: number; offline: number; off_expected: number
  maintenance: number; unknown: number; stale: number; unavailable: number
}

export interface Fenetre { origin: string; label: string | null; starts_at: string; ends_at: string; operating_from: string; operating_until: string }

export interface CarteSalle {
  code: string
  label: string
  kind: string
  power: { state: string; since: string | null; label_fr: string; situation: string; source: string }
  scheduled: boolean
  operating: boolean
  window: Fenetre | null
  next_window: Fenetre | null
  score: number | null
  score_detail: {
    evaluated: number; excluded_off_expected: number; excluded_maintenance: number; excluded_unobserved: number
    consequences: number; weights: Record<string, number>; explanation_fr: string
  }
  level: string | null
  counts: Comptes
  incidents_open: number
  data_age_s: number | null
}

export interface IncidentBref {
  id: string
  status: string
  severity: string
  title: string
  opened_at: string
  root_uid: string | null
  cause: string | null
  affected_count: number
  acked: boolean
}

export interface DocSalle {
  generated_at: string
  collector: Collecteur
  zone: CarteSalle
  power_domain: { code: string; label: string; mode: 'root' | 'vote'; status: string; root_uid: string | null; learned: boolean; learned_cycles: number } | null
  families: { family: string; label: string; counts: Comptes; devices: AppareilBref[] }[]
  incidents: IncidentBref[]
}

export interface Temoin {
  witness: string
  role: 'authority' | 'confirmation'
  source_code: string | null
  source_label: string | null
  source_status: string | null
  present: boolean | null
  observed_at: string | null
  data_age_s: number | null
  detail: Record<string, unknown> | null
}

export interface MaillonAmont { uid: string; display_name: string; kind: string; display_state: EtatAffiche; link_kind: string; port: number | string | null; attrs: Record<string, unknown> }

export interface MesureDisponible { metric: string; label: string; unit: string | null; agg: 'gauge' | 'delta' | 'state'; table: string }

export interface DerniereValeur { table: string; key: Record<string, string | number> | null; at: string; data_age_s: number; values: Record<string, number | string | null> }

export interface DocAppareil {
  generated_at: string
  collector: Collecteur
  device: AppareilBref & {
    profile_code: string | null
    affects_global_health: boolean
    tags: string[]
    notes: string | null
    first_seen_at: string | null
    last_seen_at: string | null
    created_at: string
    retired_at: string | null
    power_domain: { code: string; label: string; mode: string; status: string } | null
  }
  evidence: Temoin[]
  why_fr: string
  identifiers: { scheme: string; value: string; vlan: number | null; source: string | null; confidence: number; valid_from: string; valid_to: string | null }[]
  upstream_chain: MaillonAmont[]
  downstream_count: number
  parc: { parc_item_id: string; origin: string; confidence: number | null; confirmed_by: string | null; confirmed_at: string | null; snapshot: Record<string, unknown> | null }[]
  incidents: IncidentBref[]
  proposals: { id: string; kind: string; status: string; reason: string; created_at: string }[]
  latest: DerniereValeur[]
  series_available: MesureDisponible[]
  suggested_metrics: string[]
  /** Son alimentation pendant une coupure (contrat du 27/10/2026) ; absent d'une Sonde plus ancienne. */
  power?: AlimentationAppareil | null
}

export interface AlimentationAppareil {
  feeds: { feed: 'A' | 'B'; ups_uid: string | null; ups_label: string | null }[]
  runtime_min: number | null
  limited_by: 'ups' | 'shutdown_delay' | 'mains_only' | 'unknown'
  ups_uid: string | null
  ups_label: string | null
  text_fr: string
}

export interface EntreeChronologie { at: string; type: 'state' | 'event' | 'incident' | 'exception'; severity: string; title_fr: string; detail_fr: string | null; ref: string | null; data: Record<string, unknown> }
export interface DocChronologie { generated_at: string; uid: string; from: string; to: string; entries: EntreeChronologie[]; truncated: boolean }

export interface Voisin { relation: 'upstream' | 'downstream' | 'sibling' | 'same_zone'; link_kind: string | null; port: number | string | null; device: AppareilBref }
export interface DocVoisins { generated_at: string; uid: string; neighbors: Voisin[] }

/** Un point : [instant ms, valeur, min, max] ; valeur null = TROU marqué par Sonde. */
export type Point = [number, number | null, number | null, number | null]

export interface Serie { metric: string; label: string; unit: string | null; agg: 'gauge' | 'delta' | 'state'; table: string; key: Record<string, string | number> | null; points: Point[]; gap_threshold_s: number }
export interface DocSeries { generated_at: string; uid: string; from: string; to: string; level: string; bucket_s: number; gap_threshold_s: number; series: Serie[]; empty: string[] }

export interface Annotation { id: string; at: string; night_date: string; kind: string; kind_label: string; body: string; zone_code: string | null; device: { uid: string; display_name: string } | null; incident_id: string | null; author: { id: string; name: string }; created_at: string; deleted_at: string | null; deleted_by: string | null }
export interface DocAnnotations { items: Annotation[]; next_cursor: string | null }

export interface ResumeIncident {
  id: string
  status: 'open' | 'acknowledged' | 'recovering' | 'resolved'
  status_label: string
  severity: string
  title: string
  kind: string | null
  cause: string | null
  cause_ref: string | null
  root: { uid: string; display_name: string; kind: string; zone_code: string | null } | null
  zone_code: string | null
  affected_count: number
  opened_at: string
  acked_at: string | null
  acked_by: string | null
  recovering_at: string | null
  resolved_at: string | null
  resolved_by: string | null
  closed_reason: string | null
  duration_s: number
  notified: boolean
  black_box: { status: string; bytes: number | null } | null
  /** Sourdine (contrat 593166c, 24/10/2026) : null si aucune, échue ou incident résolu. */
  snoozed_until?: string | null
  snoozed_by?: string | null
}

export interface DocListeIncidents {
  items: ResumeIncident[]
  counts: { open: number; acknowledged: number; recovering: number; resolved_7d: number }
  next_cursor: string | null
}

export interface DonneesBoiteNoire {
  from?: string
  to?: string
  devices?: { id: string; uid: string; kind: string }[]
  series?: Record<string, Record<string, unknown>[]>
  events?: { at: string; kind: string; severity: string; device_id: string | null; source_code: string | null; payload: Record<string, unknown> | null }[]
  state_changes?: { at: string; device_id: string; from_status: string | null; to_status: string; cause?: string | null; observation?: string | null }[]
  truncated?: boolean
}

export interface BoiteNoire { id: string; captured_at: string; from: string; to: string; device_uids: string[]; size_bytes: number; expires_at: string; data: unknown; data_omitted: boolean }

export interface DocIncident {
  incident: ResumeIncident
  summary: Record<string, unknown>
  members: { uid: string; display_name: string; kind: string; zone_code: string | null; role: 'root' | 'consequence' | 'related'; added_at: string; status: string | null; observation: string | null }[]
  alerts: { id: string; state: string; severity: string; title: string; rule_code: string | null; device_uid: string | null; first_at: string; fired_at: string | null; resolved_at: string | null; acked_at: string | null; shadow: boolean }[]
  notes: { id: string; at: string; author: { id: string; name: string }; body: string }[]
  annotations: { id: string; at: string; kind: string; body: string; author: string }[]
  timeline: { at: string; kind: string; uid: string | null; severity: string | null; title_fr: string; detail: Record<string, unknown> }[]
  timeline_truncated: boolean
  black_boxes: BoiteNoire[]
}

export interface DocBoiteNoire { incident_id: string; black_boxes: BoiteNoire[] }

/* ═══════════════ Gardes de forme (minimales : ce que la page lit) ═══════════════ */

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const liste = (x: unknown): x is unknown[] => Array.isArray(x)
const texte = (x: unknown): x is string => typeof x === 'string'

const estEtat = (x: unknown) => objet(x) && texte(x['display_state'])
const estBref = (x: unknown) => objet(x) && texte(x['uid']) && texte(x['display_name']) && estEtat(x['state'])

export function estDocSalle(x: unknown): x is DocSalle {
  if (!objet(x) || !objet(x['zone']) || !liste(x['families']) || !liste(x['incidents'])) return false
  const z = x['zone']
  return texte(z['code']) && objet(z['power']) && objet(z['counts']) && objet(z['score_detail'])
    && (x['families'] as unknown[]).every((f) => objet(f) && liste(f['devices']) && (f['devices'] as unknown[]).every(estBref))
}

export function estDocAppareil(x: unknown): x is DocAppareil {
  return objet(x) && estBref(x['device']) && liste(x['evidence']) && texte(x['why_fr']) && liste(x['upstream_chain'])
    && liste(x['identifiers']) && liste(x['parc']) && liste(x['incidents']) && liste(x['proposals'])
    && liste(x['latest']) && liste(x['series_available']) && liste(x['suggested_metrics'])
}

export const estDocChronologie = (x: unknown): x is DocChronologie => objet(x) && liste(x['entries'])
export const estDocVoisins = (x: unknown): x is DocVoisins => objet(x) && liste(x['neighbors']) && (x['neighbors'] as unknown[]).every((v) => objet(v) && estBref(v['device']))
export const estDocSeries = (x: unknown): x is DocSeries => objet(x) && liste(x['series']) && liste(x['empty'])
  && (x['series'] as unknown[]).every((s) => objet(s) && liste(s['points']) && typeof s['gap_threshold_s'] === 'number')
export const estDocAnnotations = (x: unknown): x is DocAnnotations => objet(x) && liste(x['items'])

const estResume = (x: unknown) => objet(x) && texte(x['id']) && texte(x['status']) && texte(x['title']) && texte(x['opened_at'])
export const estDocListeIncidents = (x: unknown): x is DocListeIncidents => objet(x) && liste(x['items']) && objet(x['counts'])
  && (x['items'] as unknown[]).every(estResume)
export const estDocIncident = (x: unknown): x is DocIncident => objet(x) && estResume(x['incident']) && liste(x['members'])
  && liste(x['alerts']) && liste(x['notes']) && liste(x['annotations']) && liste(x['timeline']) && liste(x['black_boxes'])
export const estDocBoiteNoire = (x: unknown): x is DocBoiteNoire => objet(x) && liste(x['black_boxes'])

/* ═══════════════ Identifiants et chemins ═══════════════ */

export const UID_APPAREIL = /^[a-z0-9][a-z0-9-]{1,62}$/
export const CODE_SALLE = /^[A-Z0-9][A-Z0-9-]{0,31}$/
export const ID_INCIDENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const cheminAppareil = (uid: string) => `/infrastructure/appareils/${encodeURIComponent(uid)}`
export const cheminSalle = (code: string) => `/infrastructure/salles/${encodeURIComponent(code.toUpperCase())}`
export const cheminIncident = (id: string) => `/infrastructure/incidents/${encodeURIComponent(id)}`

/** Où valider les découvertes en attente (écran du registre, onglet « À valider »). */
export const CHEMIN_A_VALIDER = '/infrastructure/registre?onglet=a-valider'
/** Fiche d'édition d'un poste au registre. */
export const cheminRegistreAppareil = (uid: string) => `/infrastructure/registre?appareil=${encodeURIComponent(uid)}`

/** Un chemin venu de Sonde n'est suivi que s'il reste relatif et sous /infrastructure. */
export function cheminInterneSur(p: unknown): string | null {
  if (typeof p !== 'string' || p.length > 300) return null
  if (!/^\/infrastructure(?:[/?#]|$)/.test(p) || p.includes('//') || p.includes('\\') || /\.\.(?:\/|$)/.test(p)) return null
  return p
}

/* ═══════════════ Libellés ═══════════════ */

export const LIBELLE_TYPE: Record<string, string> = {
  gateway: 'Passerelle', switch: 'Switch', switch_hikvision: 'Switch Hikvision', access_point: 'Borne Wi-Fi', cloud_key: 'Cloud Key',
  camera_unifi: 'Caméra UniFi', camera_hikvision: 'Caméra Hikvision', camera_wifi: 'Caméra Wi-Fi', nvr_unifi: 'Enregistreur UniFi',
  nvr_hikvision: 'Enregistreur Hikvision', viewport: 'Viewport', speaker: 'Haut-parleur', access_hub: 'Concentrateur Access',
  access_reader: 'Lecteur Access', connect_display: 'Écran Connect', luminode: 'LumiNode', lighting_console: 'Console lumière',
  strobe: 'Strobe', lighting_fixture: 'Lumière', dmx_node: 'Nœud DMX', sound_meter: 'Sonomètre', amplifier: 'Amplificateur',
  mixer: 'Console son', dsp: 'Processeur son', dj_player: 'Lecteur DJ', dj_mixer: 'Table de mixage DJ', payment_terminal: 'TPE',
  pos_register: 'Caisse', iot_sensor: 'Capteur', smoke_detector: 'Détecteur de fumée', energy_meter: 'Compteur d’énergie',
  ups: 'Onduleur', server: 'Serveur', bmc: 'Contrôleur iDRAC', container: 'Conteneur', web_service: 'Service web', stream: 'Flux vidéo',
  printer: 'Imprimante', office_pc: 'Poste de bureau', tablet: 'Tablette', other: 'Autre',
}
export const libelleType = (k: string) => LIBELLE_TYPE[k] ?? k

export const LIBELLE_CRITICITE: Record<string, string> = {
  critical_evening: 'Critique en soirée', important: 'Important', comfort: 'Confort', info: 'Information',
}

export const LIBELLE_LIEN: Record<string, string> = {
  uplink: 'câble', wifi_ap: 'Wi-Fi', nvr_channel: 'voie NVR', poe_power: 'PoE', relay_power: 'relais', dmx_feed: 'DMX', depends_on: 'dépend de',
}

export const LIBELLE_ORIGINE_FENETRE: Record<string, string> = {
  gestion: 'Soirée de Gestion', manual: 'Fenêtre manuelle', fallback: 'Planning de secours',
}

export const LIBELLE_SOURCE_ALIM: Record<string, string> = {
  collector: 'relevé en direct', snapshot: 'dernier instantané', unavailable: 'source indisponible',
}

export const LIBELLE_RELATION: Record<Voisin['relation'], string> = {
  upstream: 'En amont', downstream: 'En aval', sibling: 'Sur le même amont', same_zone: 'Même salle, même type',
}

export const LIBELLE_ROLE: Record<string, string> = { root: 'Cause racine', consequence: 'Conséquence', related: 'Lié' }

export const LIBELLE_ETAT_ALERTE: Record<string, string> = {
  pending: 'En attente', firing: 'Active', recovering: 'En rétablissement', resolved: 'Terminée',
}

export const LIBELLE_TYPE_ANNOTATION: Record<string, string> = {
  note: 'Note', balance: 'Balances', intervention: 'Intervention', incident: 'Incident', information: 'Information',
}

/** Cause d'un état, conséquence d'abord. */
export const LIBELLE_CAUSE: Record<string, string> = {
  SELF: 'l’appareil lui-même', UPSTREAM: 'son amont est tombé', ROOM_OFF: 'la salle est coupée', MAINS_OUTAGE: 'coupure de courant',
  SOURCE: 'la source de supervision ne répond plus', EXCEPTION: 'exception posée à la main',
}

/** Ton d'un statut d'incident : ouvert = sa gravité ; pris en charge = ambre ; rétabli ou résolu = émeraude. */
export function tonStatutIncident(status: string, severity: string): Ton {
  if (status === 'open') return severity === 'critical' || severity === 'error' ? 'err' : severity === 'warning' ? 'warn' : 'gris'
  if (status === 'acknowledged') return 'warn'
  if (status === 'recovering' || status === 'resolved') return 'ok'
  return 'gris'
}

/** Ton d'un état brut d'appareil + observation : une donnée qui n'est pas fraîche est grise. */
export function tonEtatObserve(status: string | null, observation: string | null, tons: Record<string, Ton>): Ton {
  if (!status) return 'gris'
  if (observation && observation !== 'FRESH') return 'gris'
  return tons[status] ?? 'gris'
}

/**
 * Couleur de la tuile « ouverts » : la gravité la plus haute des incidents
 * ouverts listés — rose seulement pour une erreur ou un critique, ambre pour
 * un avertissement, gris pour une information ; ambre si la liste ne montre
 * pas leur gravité (filtre « Clos »). Aucun ouvert : vert.
 */
export function tonOuverts(ouverts: number, items: readonly Pick<ResumeIncident, 'status' | 'severity'>[]): Ton {
  if (ouverts <= 0) return 'ok'
  const g = new Set(items.filter((i) => i.status === 'open').map((i) => i.severity))
  if (g.has('critical') || g.has('error')) return 'err'
  if (g.has('warning')) return 'warn'
  if (g.has('info')) return 'gris'
  return 'warn'
}

/** La cause, en une ligne : qui, puis pourquoi. */
export function causeEnUneLigne(i: ResumeIncident): string {
  const pourquoi = i.cause ? LIBELLE_CAUSE[i.cause] ?? i.cause.toLowerCase() : 'cause non déterminée'
  const ref = i.cause === 'UPSTREAM' && i.cause_ref ? ` (${i.cause_ref})` : ''
  if (i.root) return `${i.root.display_name}${i.root.zone_code ? ` (${nomSalle(i.root.zone_code)})` : ''} — ${pourquoi}${ref}.`
  if (i.zone_code) return `${SALLES_CLUB[i.zone_code] ? '' : 'Salle '}${nomSalle(i.zone_code)} — ${pourquoi}${ref}.`
  return `${pourquoi.charAt(0).toUpperCase()}${pourquoi.slice(1)}${ref}.`
}

/* ═══════════════ Actions d'un incident ═══════════════ */

/**
 * Ce que Sonde accepte sur cet incident, d'après SON état (routes
 * `incidents/:id/ack|resolve|note`) : acquitter un incident résolu est
 * refusé (409 INCIDENT_RESOLVED) ; un incident déjà acquitté n'a plus rien à
 * acquitter ; résoudre un incident résolu ne change rien. La note est
 * toujours possible. Le droit de la personne (`sonde.incident.ack`) s'ajoute.
 */
export function actionsIncident(i: Pick<ResumeIncident, 'status' | 'acked_at' | 'snoozed_until'>, peutAgir: boolean): { acquitter: boolean; resoudre: boolean; noter: boolean; sourdine: boolean; lever: boolean } {
  if (!peutAgir) return { acquitter: false, resoudre: false, noter: false, sourdine: false, lever: false }
  const clos = i.status === 'resolved'
  return { acquitter: !clos && !i.acked_at, resoudre: !clos, noter: true, sourdine: !clos, lever: !clos && !!i.snoozed_until }
}

/* ═══════════════ Courbes : un trou est un trou ═══════════════ */

/**
 * Découpe une courbe en tronçons continus. Coupure : point à valeur null
 * (trou marqué par Sonde) OU écart entre deux points au-delà du seuil de la
 * courbe. Retourne aussi les plages sans donnée (début et fin de période
 * comprises), pour les montrer comme telles — jamais tracées à zéro.
 */
export function troncons(points: Point[], seuilS: number, debut?: number, fin?: number): { troncons: [number, number][][]; trous: [number, number][] } {
  const seuil = Math.max(1, seuilS) * 1000
  const out: [number, number][][] = []
  const trous: [number, number][] = []
  let cur: [number, number][] = []
  let dernier: number | null = null
  const tries = points.filter((p) => Number.isFinite(p[0])).slice().sort((a, b) => a[0] - b[0])
  for (const p of tries) {
    const [t, v] = p
    if (v === null || !Number.isFinite(v)) {
      if (cur.length) out.push(cur)
      cur = []
      continue
    }
    if (dernier !== null && t - dernier > seuil) {
      if (cur.length) out.push(cur)
      cur = []
      trous.push([dernier, t])
    } else if (dernier !== null && cur.length === 0) {
      // Reprise après un point null : la plage entre les deux vraies valeurs est un trou.
      trous.push([dernier, t])
    }
    cur.push([t, v])
    dernier = t
  }
  if (cur.length) out.push(cur)
  const premier = out[0]?.[0]?.[0]
  if (debut !== undefined && premier !== undefined && premier - debut > seuil) trous.unshift([debut, premier])
  if (fin !== undefined && dernier !== null && fin - dernier > seuil) trous.push([dernier, fin])
  if (debut !== undefined && fin !== undefined && out.length === 0) trous.push([debut, fin])
  return { troncons: out, trous }
}

/** Seuil de trou d'une suite de relevés bruts : 3 × l'écart médian (au moins `plancherS`). Même règle que Sonde. */
export function seuilTrouAdaptatif(instants: number[], plancherS = 30): number {
  if (instants.length < 3) return plancherS
  const t = instants.slice().sort((a, b) => a - b)
  const d: number[] = []
  for (let i = 1; i < t.length; i++) d.push(t[i]! - t[i - 1]!)
  d.sort((a, b) => a - b)
  return Math.max(plancherS, Math.ceil((3 * d[Math.floor(d.length / 2)]!) / 1000))
}

/** Domaine vertical : % de 0 à 100 ; sinon englobe 0 et les valeurs, avec une marge. Jamais plat. */
export function domaineY(valeurs: number[], unite: string | null): [number, number] {
  const v = valeurs.filter((x) => Number.isFinite(x))
  if (unite === '%') return [0, Math.max(100, ...v)]
  if (v.length === 0) return [0, 1]
  let lo = Math.min(0, ...v)
  let hi = Math.max(0, ...v)
  if (hi === lo) { hi = lo + 1 }
  const marge = (hi - lo) * 0.08
  if (lo < 0) lo -= marge
  hi += hi > 0 ? marge : 0
  return [lo, hi]
}

/* ═══════════════ Mesures conseillées → requêtes ═══════════════ */

/** Tables dont les courbes se déclinent par sous-clé (port, radio, voie, port DMX). */
export const SOUS_CLE: Record<string, string> = { s_port: 'port_idx', s_radio: 'radio', s_nvr_channel: 'channel', s_dmx: 'port' }
export const LIBELLE_SOUS_CLE: Record<string, string> = { s_port: 'Port', s_radio: 'Radio', s_nvr_channel: 'Voie', s_dmx: 'Port DMX' }

/** Sous-clés connues d'une table, lues dans les dernières valeurs de la fiche (ordre naturel). */
export function sousCles(latest: DerniereValeur[], table: string): string[] {
  const k = SOUS_CLE[table]
  if (!k) return []
  const vus = new Set<string>()
  for (const l of latest) {
    if (l.table !== table || !l.key) continue
    const v = l.key[k]
    if (v !== undefined && v !== null) vus.add(String(v))
  }
  return [...vus].sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))
}

/**
 * Sous-clé la plus parlante par défaut : la plus active sur la première
 * mesure choisie (dernières valeurs) ; à défaut la première.
 */
export function sousCleParDefaut(latest: DerniereValeur[], table: string, mesure: string | undefined): string | null {
  const cles = sousCles(latest, table)
  if (cles.length === 0) return null
  const col = mesure ? mesure.slice(mesure.indexOf('.') + 1) : null
  const k = SOUS_CLE[table]!
  let meilleure: string | null = null
  let max = -Infinity
  if (col) {
    for (const l of latest) {
      if (l.table !== table || !l.key) continue
      const v = l.values[col]
      if (typeof v === 'number' && Number.isFinite(v) && v > max) { max = v; meilleure = String(l.key[k]) }
    }
  }
  return meilleure ?? cles[0]!
}

/** Regroupe des mesures par table : une requête par table (la sous-clé ne vaut que pour une table). */
export function groupesParTable(mesures: string[], disponibles: MesureDisponible[]): { table: string; mesures: string[] }[] {
  const parCode = new Map(disponibles.map((d) => [d.metric, d]))
  const groupes = new Map<string, string[]>()
  for (const m of mesures) {
    const d = parCode.get(m)
    if (!d) continue
    groupes.set(d.table, [...(groupes.get(d.table) ?? []), m])
  }
  return [...groupes.entries()].map(([table, l]) => ({ table, mesures: l }))
}

/* ═══════════════ Valeurs ═══════════════ */

const nf = (n: number, max = 2) => n.toLocaleString('fr-FR', { maximumFractionDigits: max })

/** Durée lisible en secondes (« 24 min », « 3 h 05 », « 2 j 4 h ») ; « — » si inconnue. */
export function fmtDuree(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s) || s < 0) return '—'
  if (s < 60) return `${Math.round(s)} s`
  if (s < 3600) return `${Math.round(s / 60)} min`
  if (s < 86_400) { const h = Math.floor(s / 3600); const m = Math.round((s % 3600) / 60); return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h` }
  const j = Math.floor(s / 86_400); const h = Math.round((s % 86_400) / 3600)
  return h ? `${j} j ${h} h` : `${j} j`
}

/** Valeur d'une mesure avec son unité, mise à l'échelle (bit/s → Mbit/s…). « — » si absente. */
export function fmtValeur(v: number | null | undefined, unite: string | null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  switch (unite) {
    case 'bit/s': {
      const a = Math.abs(v)
      if (a >= 1e9) return `${nf(v / 1e9)} Gbit/s`
      if (a >= 1e6) return `${nf(v / 1e6)} Mbit/s`
      if (a >= 1e3) return `${nf(v / 1e3)} kbit/s`
      return `${nf(v, 0)} bit/s`
    }
    case 'Mo': return Math.abs(v) >= 1024 ? `${nf(v / 1024)} Go` : `${nf(v, 0)} Mo`
    case 's': return fmtDuree(v)
    case '%': return `${nf(v, 1)} %`
    case null: case '': return nf(v)
    default: return `${nf(v)} ${unite}`
  }
}

/** Unité devinée d'une colonne brute de boîte noire (suffixe du nom). */
export function uniteColonne(col: string): string | null {
  if (col.endsWith('_pct')) return '%'
  if (col.endsWith('_bps')) return 'bit/s'
  if (col.endsWith('_dbm')) return 'dBm'
  if (col.endsWith('_db')) return 'dB'
  if (col.endsWith('_ms')) return 'ms'
  if (col.endsWith('_mb')) return 'Mo'
  if (col.endsWith('_c')) return '°C'
  if (col.endsWith('_w')) return 'W'
  if (col.endsWith('_s')) return 's'
  return null
}

export const LIBELLE_COLONNE: Record<string, string> = {
  cpu_pct: 'CPU', mem_pct: 'Mémoire utilisée', mem_free_mb: 'Mémoire libre', temp_c: 'Température', clients: 'Clients',
  rx_bps: 'Débit reçu', tx_bps: 'Débit émis', load_avg: 'Charge', resp_ms: 'Temps de réponse', poe_used_w: 'PoE consommé',
  cu_total_pct: 'Occupation du canal', cu_interf_pct: 'Interférences', cu_self_rx_pct: 'Réception propre', cu_self_tx_pct: 'Émission propre',
  tx_retries_pct: 'Renvois', satisfaction: 'Satisfaction', rssi_dbm: 'Signal', util_pct: 'Utilisation', rx_err_d: 'Erreurs en réception',
  tx_err_d: 'Erreurs en émission', link_down_d: 'Coupures de lien', rtt_ms: 'Aller-retour', loss_pct: 'Pertes', fps: 'Trames DMX par seconde',
  max_gap_ms: 'Plus long silence DMX', laeq_db: 'Niveau sonore', charge_pct: 'Charge batterie', load_pct: 'Charge onduleur', runtime_s: 'Autonomie',
  bitrate_kbps: 'Débit de la voie', recording: 'Enregistre',
}

/** Colonnes jamais tracées dans une boîte noire : clés, textes, compteurs monotones. */
const COLONNES_EXCLUES = new Set(['ts', 'device_id', 'port_idx', 'radio', 'channel', 'port', 'state', 'uptime_s', 'hikvision_firmware', 'width_mhz', 'tx_power_dbm', 'speed_mbps', 'full_duplex', 'stp_state'])

export interface CourbeBoite { cle: string; table: string; uid: string; sous: string | null; colonne: string; label: string; unite: string | null; points: Point[]; seuilS: number }

/**
 * Courbes d'une boîte noire : par appareil, table, sous-clé et colonne
 * numérique QUI VARIE (une constante n'apprend rien). Les valeurs absentes
 * restent absentes (point null).
 */
export function courbesBoiteNoire(data: DonneesBoiteNoire): CourbeBoite[] {
  const uidDe = new Map((data.devices ?? []).map((d) => [d.id, d.uid]))
  const out: CourbeBoite[] = []
  for (const [table, lignes] of Object.entries(data.series ?? {})) {
    if (!Array.isArray(lignes) || lignes.length === 0) continue
    const sousNom = SOUS_CLE[table] ?? null
    const parEntite = new Map<string, Record<string, unknown>[]>()
    for (const l of lignes) {
      const ent = `${String(l['device_id'] ?? '')}|${sousNom ? String(l[sousNom] ?? '') : ''}`
      parEntite.set(ent, [...(parEntite.get(ent) ?? []), l])
    }
    for (const [ent, rows] of parEntite) {
      const [id, sous] = ent.split('|') as [string, string]
      const instants = rows.map((r) => Date.parse(String(r['ts']))).filter(Number.isFinite)
      const seuilS = seuilTrouAdaptatif(instants)
      const colonnes = Object.keys(rows[0] ?? {}).filter((c) => !COLONNES_EXCLUES.has(c))
      for (const col of colonnes) {
        const pts: Point[] = rows.map((r) => {
          const v = r[col]
          return [Date.parse(String(r['ts'])), typeof v === 'number' && Number.isFinite(v) ? v : null, null, null] as Point
        }).filter((p) => Number.isFinite(p[0]))
        const vals = pts.map((p) => p[1]).filter((v): v is number => v !== null)
        if (vals.length < 2 || Math.min(...vals) === Math.max(...vals)) continue
        out.push({
          cle: `${table}|${ent}|${col}`, table, uid: uidDe.get(id) ?? id, sous: sous || null, colonne: col,
          label: LIBELLE_COLONNE[col] ?? col, unite: uniteColonne(col), points: pts, seuilS,
        })
      }
    }
  }
  return out
}

/* ═══════════════ Salle : barre d'états ═══════════════ */

/** Segments de la barre d'états d'une salle ou d'une famille, dans l'ordre de gravité. Vides retirés. */
export function segmentsEtats(c: Comptes): { etat: EtatAffiche; n: number }[] {
  const ordre: [EtatAffiche, number][] = [
    ['OFFLINE', c.offline], ['DEGRADED', c.degraded], ['UNAVAILABLE', c.unavailable], ['STALE', c.stale], ['UNKNOWN', c.unknown],
    ['MAINTENANCE', c.maintenance], ['OFF_EXPECTED', c.off_expected], ['ONLINE', c.online],
  ]
  return ordre.filter(([, n]) => n > 0).map(([etat, n]) => ({ etat, n }))
}

/** Un appareil « à regarder » : ni en ligne, ni éteint comme prévu, ni en maintenance. */
export const aRegarder = (e: EtatAffiche) => e !== 'ONLINE' && e !== 'OFF_EXPECTED' && e !== 'MAINTENANCE'

/* ═══════════════ Heures de Paris ═══════════════ */

const jourParis = (d: Date) => d.toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })

/** « 23:00 » le jour même (heure de Paris), « jeu. 22/10 23:00 » sinon ; « — » si absent. */
export function fmtQuand(iso: string | null | undefined, maintenant: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
  if (jourParis(d) === jourParis(maintenant)) return heure
  const jour = d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' })
  return `${jour} ${heure}`
}
